import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { getToken } from '../lib/authToken'

interface UserSuspendedContextValue {
  isUserSuspended: boolean
}

const UserSuspendedContext = createContext<UserSuspendedContextValue>({
  isUserSuspended: false,
})

// CSS injected when body.user-suspended is set.
// Disables pointer events on mutating action buttons for suspended users.
const USER_SUSPENDED_CSS = `
body.user-suspended button[type="submit"],
body.user-suspended button:has(.lucide-plus),
body.user-suspended button:has(.lucide-trash),
body.user-suspended button:has(.lucide-trash-2),
body.user-suspended button:has(.lucide-pencil),
body.user-suspended button:has(.lucide-edit),
body.user-suspended [data-action="delete"],
body.user-suspended [data-action="save"] {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}
`

export function UserSuspendedProvider({ children }: { children: ReactNode }) {
  const [isUserSuspended, setIsUserSuspended] = useState(false)

  // Inject CSS once on mount, remove on unmount.
  useEffect(() => {
    const styleEl = document.createElement('style')
    styleEl.setAttribute('data-user-suspended-styles', '')
    styleEl.textContent = USER_SUSPENDED_CSS
    document.head.appendChild(styleEl)
    return () => styleEl.remove()
  }, [])

  // Toggle body class so CSS selectors can disable mutating elements.
  useEffect(() => {
    if (isUserSuspended) {
      document.body.classList.add('user-suspended')
    } else {
      document.body.classList.remove('user-suspended')
    }
    return () => document.body.classList.remove('user-suspended')
  }, [isUserSuspended])

  useEffect(() => {
    const token = getToken()
    if (!token) return

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      setIsUserSuspended(payload.suspended === true)
    } catch {
      // JWT decode failed — default to not suspended
    }
  }, [])

  return (
    <UserSuspendedContext.Provider value={{ isUserSuspended }}>
      {children}
    </UserSuspendedContext.Provider>
  )
}

export function useUserSuspended() {
  return useContext(UserSuspendedContext)
}
